import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert, Button, Card, PageHeader, Spinner } from '@platform/ui';

import { FormVersions } from '../../../features/forms/versions-view';
import { useFormsClient } from '../../../lib/api/forms-client-context';

export const Route = createFileRoute('/forms/$formId/versions')({
  component: VersionsPage,
});

/** `/forms/:formId/versions` — the published history (Docs/API.md §2.8, FR-INTAKE-04). */
function VersionsPage() {
  const { formId } = Route.useParams();
  const client = useFormsClient();
  const [selected, setSelected] = useState<number | null>(null);

  const detail = useQuery({
    queryKey: ['intake-forms', formId],
    queryFn: () => client.get(formId),
  });

  const version = useQuery({
    queryKey: ['intake-forms', formId, 'version', selected],
    enabled: selected !== null,
    queryFn: () => client.getVersion(formId, selected ?? 1),
  });

  if (detail.isPending) {
    return (
      <>
        <PageHeader title="Versions" />
        <Card className="p-6">
          <Spinner label="Loading versions" />
        </Card>
      </>
    );
  }

  if (detail.isError) {
    return (
      <>
        <PageHeader title="Versions" />
        <Alert tone="error">This form could not be loaded.</Alert>
      </>
    );
  }

  return (
    <FormVersions
      form={detail.data.form}
      versions={detail.data.versions}
      selected={selected}
      onSelect={setSelected}
      schema={version.data?.schema ?? null}
      loading={selected !== null && version.isPending}
      error={
        version.isError
          ? version.error instanceof Error
            ? version.error.message
            : 'That version could not be loaded.'
          : undefined
      }
      builderLink={
        <Link to="/forms/$formId" params={{ formId }}>
          <Button variant="secondary">Back to the builder</Button>
        </Link>
      }
    />
  );
}
